import * as React from 'react';
import {
  ForwardedRef,
  forwardRef,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CheckStatus,
  IData,
  VirtualDataRef,
} from '../virtual-data/VirtualData';
import { DataTableMode } from '../common/props';
import { useCheckIds } from '../rows-data/useCheckIds';
import { useCurrentTheme } from '../themes';
import { CheckboxButton } from '../checkbox/CheckboxButton';
import { RenderedSection } from 'react-virtualized/dist/es/Grid';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import {
  AutoSizer,
  Grid,
  IndexRange,
  InfiniteLoader,
  ScrollSync,
  SizeInfo,
} from 'react-virtualized';
import { Spinner } from '../spinner/Spinner';
import { InfiniteLoaderChildProps } from 'react-virtualized/dist/es/InfiniteLoader';
import { NoData } from '../no-data/NoData';
import {
  FinalTableCommonRenderer,
  FinalTableComputedColumn,
  FinalTableGetRowPropsType,
  FinalTableProps,
} from './FinalTableProps';
import {
  DEFAULT_COLUMN_WIDTH,
  getColumnFieldNoCheck,
  getColumnPixelWidth,
  getComputeColumnsWidth,
  getFormattedColumns,
  GROUP_COLUMN_ID,
  MIN_COL_WIDTH,
  useHeights,
} from './utils';
import { BodyRow } from './BodyRow';
import { HeaderRow } from './HeaderRow';
import { FooterRow } from './FooterRow';
import { Filters } from '../filter/Filters';
import { Card } from '../card/Card';
import { Block } from '../block/Block';
import { AppError } from '../error/AppError';
import { RowsCount } from './RowsCount';
import {
  getFinalTableFilterData,
  getFinalTableFilterDataKey,
  getFinalTableProfileData,
  getFinalTableProfileDataKey,
  TableSettings,
  useTableSettings,
} from './TableSettings';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { mcClose } from '@mc/react-ui-icons';
import { InverseBgProvider } from '../theme/InverseBg';
import { Divider } from '../divider/Divider';
import { ColumnInfo } from '../rows-data/types';
import { ResizeType } from './ColumnResizer';
import { isEmpty } from 'lodash-es';
import { ITitleProps } from '../typography/Title';
import { FinalTableTitle } from './FinalTableTitle';
import { formatNumber } from '@mc/react-format';
import { Text } from '../typography/Text';
import { TableExport } from './TableExport';

type Props<DATA extends IData<ID>, FILTER, ID> = {
  /**
   * Nadpis tabulky - ve výchozím stavu se zobrazuje velikostí cardTitle.
   * POZOR - Předaný string se předává jako obsah do komponenty Title.
   */
  title?: string;
  titleProps?: Omit<ITitleProps, 'children'>;

  /**
   * Výška patičky tabulky.
   */
  footerHeight?: number;

  /**
   * Výška záhlaví tabulky.
   */
  headerHeight?: number;

  /**
   * Který řádek má být zvýrazněn - dle id.
   */
  highlightId?: ID;

  /**
   * Který řádek má být zvýrazněn - dle indexu řádku.
   */
  highlightRowIndex?: number;

  /**
   * Výchozí šířka sloupce, pokud není u sloupce žádná šířka definována.
   */
  defaultColumnWidth?: number;

  /**
   * Výška řádku.
   */
  rowHeight?: number;

  /**
   * Výška seskupovacího řádku.
   */
  groupRowHeight?: number;

  /**
   * Vizuální podoba tabulky.
   */
  mode?: DataTableMode;

  // TODO CHDI?
  noVerticalScroll?: boolean;

  /**
   * Skryje hlavičku tabulky (počet záznamů, export, nastavení).
   */
  noHeader?: boolean;

  /**
   * Sekce pro zobrazení extra vybraných položek + akcí apod., zobrazuje se POUZE pokud je něco vybráno.
   * @param props
   */
  extra?: (props: FinalTableCommonRenderer<ID>) => ReactNode;

  /**
   * Sekce pro zobrazení extra informací + akcí apod., zobrazuje se VŽDY.
   * @param props
   */
  extraGlobal?: (props: FinalTableCommonRenderer<ID>) => ReactNode;

  /**
   * Sekce pro zobrazení extra komponenty nad filtery, zobrazuje se VŽDY pokud je definováno.
   * @param props
   */
  fixedFilterAddon?: (props: FinalTableCommonRenderer<ID>) => ReactNode;

  /**
   * Pokud je true, tak se vždy zobrazují extra a extraGlobal akce a vlastně hlavička tabulky je stejná jako když mám něco vybráno - tedy tmavá.
   */
  alwaysShowExtra?: boolean;

  /**
   * Třída pro celou tabulku.
   */
  className?: string;

  /**
   * Vypne hover, spuštěný automaticky na řádcích s onClick.
   */
  rowDisableHover?: boolean;

  /**
   * Funkce pro získání props pro datový řádek.
   */
  getRowProps?: FinalTableGetRowPropsType<DATA>;
  /**
   * Funkce pro získání props pro group řádek.
   */
  getGroupRowProps?: FinalTableGetRowPropsType<DATA>;

  /**
   * Props pro tabulku, které se netýkají vzhledu, ale funkčnosti.
   */
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
};

const emptyArr: any[] = [];

const FinalTableIn = <
  DATA extends IData<ID>,
  FILTER extends object = {},
  ID extends unknown = {},
>(
  {
    className,
    highlightId,
    highlightRowIndex,
    rowHeight,
    defaultColumnWidth = 120,
    rowDisableHover,
    headerHeight,
    extra,
    extraGlobal,
    fixedFilterAddon,
    alwaysShowExtra,
    title,
    titleProps,
    mode = 'default',
    noVerticalScroll,
    noHeader,
    footerHeight,
    getRowProps,
    getGroupRowProps,
    finalTableProps,
    groupRowHeight,
  }: Props<DATA, FILTER, ID>,
  // @ts-ignore
  ref: ForwardedRef<VirtualDataRef<ID>>
) => {
  const theme = useCurrentTheme();
  const isGrouping = finalTableProps.isGrouping();
  // CelKový počet řádků (jakýchkoliv) v tabulce
  const totalTableRows = finalTableProps.totalElements ?? 0;
  const [visualRerender, setVisualRerender] = useState<number>(0);
  const [lastCheckbox, setLastCheckbox] = useState<number>();
  const hasVerticalScroll = useRef<boolean>(false);
  const [_, setTableSettings] = useTableSettings(
    finalTableProps.config.settingsKey
  );

  useEffect(() => {
    if (
      !finalTableProps.config.settingsKey ||
      !finalTableProps.config.autoSaveSettings
    ) {
      return;
    }

    const profileData = getFinalTableProfileData(finalTableProps);
    const filterData = getFinalTableFilterData(finalTableProps);

    setTableSettings({
      profileData,
      filterData,
    });
  }, [
    finalTableProps.config.settingsKey,
    finalTableProps.config.autoSaveSettings,
    ...getFinalTableProfileDataKey(finalTableProps),
    ...getFinalTableFilterDataKey(finalTableProps),
  ]);

  const [showErrors, setShowErrors] = useState<boolean>(false);
  useEffect(() => {
    if (finalTableProps.loading) {
      setShowErrors(false);
    } else {
      setLastCheckbox(undefined);
    }
  }, [finalTableProps.loading]);

  const hasData =
    finalTableProps.totalElements !== undefined &&
    finalTableProps.errors.length === 0
      ? finalTableProps.totalElements > 0
      : false;

  const ids = finalTableProps.ids || emptyArr;

  const checkIds = useCheckIds<ID>(
    isGrouping ? undefined : ids,
    finalTableProps.checkedIds,
    finalTableProps.setCheckedIds
  );

  const commonsProps: FinalTableCommonRenderer<ID> = {
    headerProps: {
      checkedStatus: checkIds.checkedStatus,
      onToggleCheckAll: checkIds.onToggleCheckAll,
      checkable: !!finalTableProps.config.checkable,
      disabled: finalTableProps.loading,
    },
    checkAll: checkIds.checkAll,
    checkedIds: finalTableProps.checkedIds,
    hardFilter: finalTableProps.hardFilter,
  };

  const heights = useHeights(
    rowHeight,
    headerHeight,
    groupRowHeight,
    footerHeight
  );

  const formattedColumns = useMemo(() => {
    let resCols = getFormattedColumns(finalTableProps);

    // Pokud máme zaškrtávací sloupec, dáma ho ZA group sloupce
    let checkCol: FinalTableComputedColumn<DATA> | undefined = undefined;
    if (finalTableProps.config.checkable) {
      checkCol = {
        check: true,
        sortable: false,
        groupable: false,
        type: '_check',
        header: isGrouping ? undefined : (
          <CheckboxButton
            // @ts-ignore
            onChange={() => checkIds.onToggleCheckAll()}
            disabled={!hasData}
            indeterminate={checkIds.checkedStatus === CheckStatus.SOME_CHECKED}
            checked={
              checkIds.checkedStatus === CheckStatus.ALL_CHECKED
                ? true
                : checkIds.checkedStatus === CheckStatus.ALL_UNCHECKED
                  ? false
                  : undefined
            }
          />
        ),
        width:
          theme.checkboxButtonSize + (theme.tableCellHorizontalPadding * 2) / 2, // pro tento sloupec chceme poloviční padding, protože checkbox už nějaký má sám o sobě
        cell: ({ rowIndex }) => {
          const isGroupRow = finalTableProps.isGroupByIndex(rowIndex);
          if (isGroupRow) {
            const isGroupSelectable =
              finalTableProps.isGroupSelectable(rowIndex);
            if (!isGroupSelectable) return null;

            return (
              <div onClick={(e) => e.stopPropagation()}>
                <CheckboxButton
                  // @ts-ignore
                  onChange={(e) => {
                    const checked = (e.target as HTMLInputElement).checked;
                    finalTableProps.checkGroup(rowIndex, checked);
                  }}
                  checked={finalTableProps.isGroupChecked(rowIndex)}
                />
              </div>
            );
          } else {
            const rowRenderProps = finalTableProps.getRowRenderProps(rowIndex);
            rowRenderProps.onCheck = () => checkIds.onCheck(rowRenderProps.id);
            rowRenderProps.checkable = finalTableProps.config.checkable;
            rowRenderProps.checked = checkIds.isChecked(rowRenderProps.id);
            rowRenderProps.highlight =
              highlightId === rowRenderProps.id ||
              highlightRowIndex === rowIndex;

            return (
              <div onClick={(e) => e.stopPropagation()}>
                <CheckboxButton
                  // @ts-ignore
                  onChange={(e) => {
                    // @ts-ignore
                    if (e?.shiftKey && lastCheckbox != null) {
                      const itemsToCheck = [];
                      const start = Math.min(lastCheckbox, rowIndex);
                      const end = Math.max(lastCheckbox, rowIndex);

                      for (let i = start; i <= end; i++) {
                        if (
                          i !== lastCheckbox &&
                          !finalTableProps.isGroupByIndex(i)
                        ) {
                          const rowProps = finalTableProps.getRowRenderProps(i);
                          itemsToCheck.push(rowProps.id);
                        }
                      }

                      checkIds.check(itemsToCheck, true);
                    } else {
                      rowRenderProps.onCheck?.();
                    }
                    setLastCheckbox(rowIndex);
                  }}
                  disabled={rowRenderProps.isDeleted}
                  checked={rowRenderProps.isDeleted ? false : rowRenderProps.checked}
                />
              </div>
            );
          }
        },
      };
    }
    if (checkCol) {
      if (finalTableProps.grouping.length > 0) {
        // Je seskupení, první sloupec jsou skupiny
        resCols.splice(1, 0, checkCol);
      } else {
        // Není seskupení
        resCols.splice(0, 0, checkCol);
      }
      return resCols;
    } else {
      return resCols;
    }
  }, [
    theme,
    finalTableProps.config.columns,
    finalTableProps.columns,
    finalTableProps.config.checkable,
    finalTableProps.grouping,
    finalTableProps.sort,
    finalTableProps.aggregate,
    checkIds.checkedIds,
    lastCheckbox,
    hasData,
  ]);

  const columnResizing = useRef<ResizeType | null>(null);

  /**
   * metoda pro resize sloupce - spouští se při onMouseDown na ColumnResizer
   * setuje pozici a index
   * @param finalTableProps
   * @param formattedColumns
   * @param position
   * @param identifikator
   * @param widths
   */
  const setColumnResize = (
    finalTableProps: FinalTableProps<DATA, FILTER, ID>,
    formattedColumns: FinalTableComputedColumn<DATA>[],
    position: number,
    identifikator: string,
    widths: { [key: number]: number }
  ) => {
    let identifikatorIndex = undefined;
    let identifikatorColumnWidth = undefined;

    //při kliknutí je nejprve nutné převést všechny šířky na pixely
    const originalColumns: ColumnInfo[] = [];
    if (!isEmpty(finalTableProps?.columns)) {
      //pokud jsou již ColumnInfo vyplněné, můžeme si je zkopírovat a jen upravit šířky dle aktuálního zobrazení
      identifikatorIndex = formattedColumns.findIndex(
        (column) => getColumnFieldNoCheck(column) === identifikator
      );
      identifikatorColumnWidth = widths[identifikatorIndex];
      // console.log(
      //   'FIND',
      //   formattedColumns,
      //   identifikator,
      //   identifikatorIndex,
      //   identifikatorColumnWidth,
      //   widths
      // );
    } else {
      //v případě že ColumnInfo nemáme, nutno si poradit jinak - pracujeme s formattedColumns (tj. pouze zobrazované sloupce)
      formattedColumns.forEach((item, index) => {
        if (item.group) {
          //pro grupu nutno nastavit externě
        } else if (item.type !== 'action') {
          let field = getColumnFieldNoCheck(item);

          //pro negrupove polozky to proste nahazej do toho pole
          if (field) {
            originalColumns.push({
              ident: field,
              visible: true,
              width: widths[index],
            });

            if (identifikator === field) {
              identifikatorIndex = index;
              identifikatorColumnWidth = widths[index];
            }
          }
        }
      });
    }

    // console.log(
    //   'SET COLUMN RESIZE',
    //   identifikatorIndex,
    //   identifikatorColumnWidth,
    //   originalColumns,
    //   newGroupColumnWidth,
    //   'columnsWidth',
    //   widths
    // );

    // if (newGroupColumnWidth !== undefined) {
    //   finalTableProps?.setGroupColumnWidth(newGroupColumnWidth);
    // }

    // Pokud je groupováno a mění group sloupec, nastavíme správně hodnoty
    if (isGrouping && identifikator === GROUP_COLUMN_ID) {
      identifikatorIndex = 0;
      identifikatorColumnWidth = widths[0];
    }

    // pokud se dohledal index, ulož si upravovaný prvek
    if (
      identifikatorIndex !== undefined &&
      identifikatorColumnWidth !== undefined
    ) {
      // finalTableProps?.setColumns(originalColumns);

      columnResizing.current = {
        index: identifikatorIndex,
        position,
        width: identifikatorColumnWidth,
        columnsWidth: widths,
      };

      setVisualRerender((x) => x + 1);
    } else {
      console.error(
        `Nebylo možné dohledat sloupec s indetifikátorem ${identifikator}`
      );
    }
  };

  /**
   * metoda pro resize sloupce - spouští se při onMouseUp na ColumnResizer
   * clearuje pozici a index
   */
  const clearColumnResize = () => {
    if (!columnResizing.current) {
      return;
    }

    let newValues: ColumnInfo[] | undefined = undefined;
    let groupColumnWidth = undefined;
    if (finalTableProps.columns.length === 0) {
      newValues = [];

      formattedColumns.forEach((col, index) => {
        if (col.group) {
          groupColumnWidth = columnResizing.current?.columnsWidth[index];
          col.groupColumns?.forEach((groupCol) => {
            let field = getColumnFieldNoCheck(groupCol);
            if (field) {
              // @ts-ignore
              newValues.push({
                ident: field,
                visible: true,
                width:
                  getColumnPixelWidth(groupCol.width) || DEFAULT_COLUMN_WIDTH,
              });
            }
          });
        } else if (col.type != 'action') {
          let field = getColumnFieldNoCheck(col);
          if (field) {
            // @ts-ignore
            newValues.push({
              ident: field,
              visible: true,
              width: columnResizing.current?.columnsWidth[index],
            });
          }
        }
      });
    } else {
      newValues = [...finalTableProps.columns];
      formattedColumns.forEach((col, index) => {
        if (col.group) {
          groupColumnWidth = columnResizing.current?.columnsWidth[index];
        } else {
          const field = getColumnFieldNoCheck(col);
          if (field) {
            // @ts-ignore
            const colIndex = newValues.findIndex((x) => x.ident === field);
            if (colIndex >= 0) {
              // @ts-ignore
              newValues[colIndex] = {
                // @ts-ignore
                ...newValues[colIndex],
                width: columnResizing.current?.columnsWidth[index],
              };
            }
          }
        }
      });
    }

    // console.log('UP COLS', newValues);
    columnResizing.current = null;

    finalTableProps.setColumns(newValues);
    if (groupColumnWidth !== undefined) {
      finalTableProps.setGroupColumnWidth(groupColumnWidth);
    }
  };

  /**
   * metoda pro resize sloupce - spouští se při jakémkoliv pohybu myši, ukládá si do statu novou šířku sloupců
   * @param e
   * @param finalTableProps
   */
  const setColumnResizeValue = (e: MouseEvent) => {
    if (columnResizing.current) {
      const { position, index, width, columnsWidth } = columnResizing.current!;

      const current = e.clientX;

      // const resizeElement = columnsWidth[index];

      let newSize = width - (position - current);
      if (newSize < MIN_COL_WIDTH) newSize = MIN_COL_WIDTH;
      columnsWidth[index] = newSize;

      // finalTableProps?.setColumns(newValues);
      setVisualRerender((x) => x + 1);
    }
  };

  /**
   * useEffect pro trackování polohy a akcí myši - nutné kvůli resize sloupců
   */
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setColumnResizeValue(e);
    };
    const handleMouseUp = () => {
      clearColumnResize();
    };

    // @ts-ignore
    document.addEventListener('mousemove', handleMouseMove);
    // @ts-ignore
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      // @ts-ignore
      document.removeEventListener('mousemove', handleMouseMove);
      // @ts-ignore
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [finalTableProps, formattedColumns]);

  const scrollbarWidth = noVerticalScroll
    ? 0
    : useCurrentTheme().scrollbarWidth;

  const computeColumnsWidth = (
    areaWidth: number,
    isGrouping: boolean,
    columns: FinalTableComputedColumn<DATA>[],
    columnsInfo: ColumnInfo[]
  ) => {
    const columnsInfoMap: { [key: string]: ColumnInfo } = {};

    columnsInfo.forEach((ci) => {
      columnsInfoMap[ci.ident] = ci;
    });

    // Zmenšení oblasti o scrollbar
    const areaWidthWithoutScrollbar = areaWidth - scrollbarWidth;

    // Pokud máme grouping, spočteme šířku prvního sloupečku, pokud už má známou šířku v groupColumnWidth, rovnou použijeme tuto hodnotu, jinak spočteme kolik by asi měl
    let groupColumnPixelWidth = undefined;
    if (isGrouping) {
      const groupColumn = columns.find((x) => x.group);
      if (groupColumn) {
        if (finalTableProps.groupColumnWidth !== undefined) {
          groupColumnPixelWidth = finalTableProps.groupColumnWidth;
        } else {
          const res = getComputeColumnsWidth(
            areaWidth,
            columns,
            columnsInfoMap,
            theme,
            areaWidthWithoutScrollbar,
            defaultColumnWidth
          );

          const maxColWidth = res.groupColumnsWidth.reduce(
            (a, b) => Math.max(a, b),
            0
          );
          // console.log('MAX col width', res.groupColumnsWidth, maxColWidth);

          const grpColsLength = groupColumn.groupColumns?.length || 0;
          let colWidth =
            maxColWidth +
            (grpColsLength - 1) * theme.finalTableGroupDepthPadding +
            grpColsLength * theme.finalTableHeaderActionsSize +
            theme.finalTableGroupExpColWidth;

          groupColumnPixelWidth = colWidth;
        }
      }
    }

    const res = getComputeColumnsWidth(
      areaWidth,
      columns,
      columnsInfoMap,
      theme,
      areaWidthWithoutScrollbar,
      defaultColumnWidth,
      groupColumnPixelWidth
    );

    return res.widthsMap;
  };

  const onSectionRendered = (
    props: RenderedSection,
    onRowsRendered: (params: IndexRange) => void
  ) => {
    // console.log('SECTIOn redered', props.rowStartIndex, props.rowStopIndex);
    finalTableProps.onSectionRendered({
      startIndex: props.rowStartIndex,
      stopIndex: props.rowStopIndex,
    });
    onRowsRendered({
      startIndex: props.rowStartIndex,
      stopIndex: props.rowStopIndex,
    });
  };

  const gridRef = useRef<Grid | null>(null);
  const sourceScrollbarWidth = useCurrentTheme().scrollbarWidth;

  const [needScrollToRow, setNeedScrollToRowIndex] = useState<
    number | undefined
  >(undefined);

  const lastVisitedRowIndexRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (hasData && highlightId !== undefined) {
      const i = ids.indexOf(highlightId);
      if (i >= 0) {
        setNeedScrollToRowIndex(i);
      }
    }
  }, [highlightId, hasData]);
  useEffect(() => {
    if (hasData && highlightRowIndex !== undefined) {
      setNeedScrollToRowIndex(highlightRowIndex);
    }
  }, [highlightRowIndex, hasData]);
  useEffect(() => {
    if (
      hasData &&
      // !finalTableProps.loading &&
      finalTableProps.lastVisitedRowIndex !== undefined &&
      finalTableProps.lastVisitedRowIndex !== lastVisitedRowIndexRef.current
    ) {
      setNeedScrollToRowIndex(finalTableProps.lastVisitedRowIndex);
    }
  }, [
    finalTableProps.totalElements,
    finalTableProps.lastVisitedRowIndex,
    hasData,
    // finalTableProps.loading,
  ]);

  const getScrollToHighlight = useCallback(() => {
    setNeedScrollToRowIndex(undefined);
    let number = needScrollToRow === undefined ? -1 : needScrollToRow;
    return number;
    // let resultScrollToIndex;
    //
    // if (
    //   highlightId !== undefined &&
    //   highlightId !== null &&
    //   scrollToHighlight &&
    //   hasData &&
    //   ids
    // ) {
    //   setScrollToHighlight(false);
    //   resultScrollToIndex = ids.indexOf(highlightId);
    // } else if (
    //   highlightRowIndex !== undefined &&
    //   highlightRowIndex !== null &&
    //   scrollToHighlight &&
    //   hasData
    // ) {
    //   resultScrollToIndex = highlightRowIndex;
    // } else if (finalTableProps.lastVisitedRowIndex) {
    //   resultScrollToIndex = finalTableProps.lastVisitedRowIndex;
    // } else {
    //   resultScrollToIndex = undefined;
    // }
    // console.log('ETURN resultScrollToIndex', resultScrollToIndex);
    // return resultScrollToIndex;
  }, [needScrollToRow]);

  // ???????????????????????????
  // useEffect(() => {
  //   if (gridRef.current && pageNumber && pageNumber > 0) {
  //     //nejsem si uplně jistej co jsem tady dělal, ale jinak jsem to nevymyslel
  //     //@ts-ignore  použit ts-ignore, protože funkce tvrdí, že je potřeba předat i scrollLeft ale není
  //     gridRef.current?.scrollToPosition({ scrollTop: 0 });
  //   }
  // }, [pageNumber]);

  const someChecked = checkIds.checkedIds.length > 0;

  const extraGlobalProps: FinalTableCommonRenderer<ID> = {
    ...commonsProps,
  };
  const extraProps: FinalTableCommonRenderer<ID> = {
    ...commonsProps,
    headerProps: {
      ...commonsProps.headerProps,
      disabled: commonsProps.headerProps.disabled || !someChecked,
    },
  };

  return (
    <div className={classNames(Classes.FINAL_TABLE_WRAPPER, className)}>
      {(finalTableProps.config.filters || title) && (
        <div className={classNames(Classes.FINAL_TABLE_WHEADER)}>
          {title && (
            <FinalTableTitle
              title={title}
              titleProps={titleProps}
              finalTableProps={finalTableProps}
            />
          )}

          {fixedFilterAddon && (
            <div className={'mb-1'}>{fixedFilterAddon?.(extraGlobalProps)}</div>
          )}

          {finalTableProps.config.filters && (
            <Card mode={'filter'} className={'mb-1'}>
              <Block>
                <Filters<FILTER>
                  filtersDef={finalTableProps.config.filters.filtersDef}
                  filterByFilled={finalTableProps.config.filters.filterByFilled}
                  extendedFilter={finalTableProps.config.filters.extendedFilter}
                  type={finalTableProps.config.filters.type}
                  setFilter={finalTableProps.setFilter}
                  formFilter={finalTableProps.formFilter}
                  visibleFields={finalTableProps.filterVisibleFields}
                  setVisibleFields={finalTableProps.setFilterVisibleFields}
                  getClearFilter={finalTableProps.getClearFilter}
                  settingsKey={finalTableProps.config.settingsKey}
                />
              </Block>
            </Card>
          )}
        </div>
      )}
      <div
        className={classNames(Classes.FINAL_TABLE_CONTAINER, {
          [Classes.FINAL_TABLE_MODE_DEFAULT]: mode === 'default',
          [Classes.FINAL_TABLE_MODE_INPUT]: mode === 'input',
          [Classes.FINAL_TABLE_NO_DATA]: !hasData,
        })}
        style={{ position: 'relative' }}
      >
        {!noHeader && (
          <div className={classNames(Classes.FINAL_TABLE_EXTRA_CONTAINER)}>
            <div
              className={classNames(Classes.FINAL_TABLE_EXTRA, {
                [Classes.FINAL_TABLE_EXTRA_CHM]:
                  someChecked || alwaysShowExtra,
              })}
            >
              {!alwaysShowExtra && !someChecked && (
                <div className={Classes.FINAL_TABLE_EXTRA_LEFT}>
                  <div className={Classes.FINAL_TABLE_EXTRA_CONTENT}>
                    <div className={Classes.FINAL_TABLE_EXTRA_RC}>
                      <RowsCount
                        totalDataElements={finalTableProps.totalDataElements}
                      />
                    </div>
                    {extraGlobal?.(extraGlobalProps)}
                  </div>
                </div>
              )}
              {!alwaysShowExtra && someChecked && (
                <div className={Classes.FINAL_TABLE_EXTRA_LEFT}>
                  <div className={Classes.FINAL_TABLE_EXTRA_CONTENT}>
                    <InverseBgProvider inverse>
                      <Button
                        disabled={finalTableProps.loading}
                        style={'link'}
                        icon={<Icon icon={mcClose} />}
                        onClick={() => checkIds.checkAll(false)}
                      >
                        Vybráno: {formatNumber(checkIds.checkedIds.length)}
                      </Button>
                      <Divider />
                      {extra?.(extraProps)}
                      {extraGlobal?.(extraGlobalProps)}
                    </InverseBgProvider>
                  </div>
                </div>
              )}
              {alwaysShowExtra && (
                <div className={Classes.FINAL_TABLE_EXTRA_LEFT}>
                  <div className={Classes.FINAL_TABLE_EXTRA_CONTENT}>
                    <InverseBgProvider inverse>
                      <div className={'d-flex align-items-center'}>
                        {someChecked && (
                          <Button
                            disabled={finalTableProps.loading}
                            style={'link'}
                            icon={
                              <Icon
                                icon={mcClose}
                                style={{ fontSize: '80%' }}
                              />
                            }
                            onClick={
                              someChecked
                                ? () => checkIds.checkAll(false)
                                : undefined
                            }
                          ></Button>
                        )}
                        <Text
                          type={'value'}
                          className={classNames('pr-1', {
                            ['pl-1']: !someChecked,
                          })}
                        >
                          Vybráno: {formatNumber(checkIds.checkedIds.length)} /{' '}
                          {formatNumber(finalTableProps.totalDataElements ?? 0)}
                        </Text>
                      </div>
                      <Divider />
                      {extra?.(extraProps)}
                      {extraGlobal?.(extraGlobalProps)}
                    </InverseBgProvider>
                  </div>
                </div>
              )}
              <div className={Classes.FINAL_TABLE_EXTRA_RIGHT}>
                {(finalTableProps.config.exportable?.all ||
                  finalTableProps.config.exportable?.excel) && (
                  <InverseBgProvider inverse={someChecked || alwaysShowExtra}>
                    <TableExport finalTableProps={finalTableProps} />
                  </InverseBgProvider>
                )}
                {!alwaysShowExtra && !someChecked && (
                  <TableSettings<DATA, FILTER, ID>
                    finalTableProps={finalTableProps}
                    settingsKey={finalTableProps.config.settingsKey}
                  />
                )}
                {alwaysShowExtra && (
                  <InverseBgProvider inverse>
                    <TableSettings<DATA, FILTER, ID>
                      finalTableProps={finalTableProps}
                      settingsKey={finalTableProps.config.settingsKey}
                    />
                  </InverseBgProvider>
                )}
              </div>
            </div>
          </div>
        )}
        <div
          className={classNames(Classes.FINAL_TABLE, {
            [Classes.FINAL_TABLE_NO_VERTICAL_SCROLL]: noVerticalScroll,
            [Classes.FINAL_TABLE_GROUPING]: isGrouping,
          })}
        >
          {finalTableProps.loading && (
            <div className={Classes.FINAL_TABLE_LOADING_CONTAINER}>
              <Spinner />
            </div>
          )}
          <ScrollSync>
            {({ onScroll, scrollLeft }) => (
              <AutoSizer disableHeight={noVerticalScroll}>
                {({ width, height }: SizeInfo) => {
                  if (noVerticalScroll) {
                    height =
                      heights.headerHeight + heights.rowHeight * totalTableRows;
                  }

                  const columnsWidth = computeColumnsWidth(
                    width,
                    isGrouping,
                    formattedColumns,
                    finalTableProps.columns
                  );

                  let rowColumnWidth = 0;
                  Object.values(columnsWidth).forEach((v) => {
                    rowColumnWidth += v;
                  });
                  // Pokud je šířka buňky která reprezentuje všechny buňky hlavičky menší než šířka tabulky, zvětšíme ji - jinak by při resize sloupců nebyly sloupce, které se dostaly mimo buňku hlavičky, vidět
                  if (rowColumnWidth < width) {
                    rowColumnWidth = width;
                  }

                  rowColumnWidth -= scrollbarWidth;
                  const horizontalScrollBarHeight =
                    rowColumnWidth > width ? sourceScrollbarWidth : 0;

                  const areaWidth = width - scrollbarWidth;
                  const showKebabOnHover =
                    Math.round(rowColumnWidth) > areaWidth;

                  return (
                    <>
                      <Grid
                        className={classNames(Classes.FINAL_TABLE_HEADER)}
                        cellRenderer={(props) => (
                          <HeaderRow
                            finalTableProps={finalTableProps}
                            formattedColumns={formattedColumns}
                            columnResizingIndex={columnResizing.current?.index}
                            columnsWidth={
                              columnResizing.current
                                ? columnResizing.current.columnsWidth
                                : columnsWidth
                            }
                            noVerticalScroll={!!noVerticalScroll}
                            gridCellProps={props}
                            heights={heights}
                            setColumnResize={setColumnResize}
                          />
                        )}
                        columnWidth={
                          rowColumnWidth + columnsWidth[formattedColumns.length]
                        }
                        columnCount={1}
                        overscanColumnCount={1}
                        __rerender={finalTableProps.rerender + visualRerender}
                        height={heights.headerHeight}
                        width={width}
                        rowCount={1}
                        rowHeight={heights.headerHeight}
                        scrollLeft={scrollLeft}
                      />
                      {hasData ? (
                        <>
                          <InfiniteLoader
                            ref={finalTableProps.infiniteLoaderRef}
                            rowCount={totalTableRows}
                            loadMoreRows={finalTableProps.loadMoreRows}
                            isRowLoaded={finalTableProps.isRowLoaded}
                            minimumBatchSize={1}
                            threshold={15}
                          >
                            {({
                              onRowsRendered,
                              registerChild,
                            }: InfiniteLoaderChildProps) => (
                              <Grid
                                className={classNames(
                                  Classes.FINAL_TABLE_CONTENT
                                )}
                                onScrollbarPresenceChange={({ vertical }) => {
                                  hasVerticalScroll.current = vertical;
                                }}
                                cellRenderer={(props) => {
                                  return (
                                    <BodyRow
                                      areaWidth={
                                        areaWidth +
                                        (hasVerticalScroll.current
                                          ? 0
                                          : scrollbarWidth)
                                      }
                                      scrollLeft={scrollLeft}
                                      finalTableProps={finalTableProps}
                                      formattedColumns={formattedColumns}
                                      checkIds={checkIds}
                                      heights={heights}
                                      gridCellProps={props}
                                      columnsWidth={columnsWidth}
                                      rowDisableHover={!!rowDisableHover}
                                      getRowProps={getRowProps}
                                      getGroupRowProps={getGroupRowProps}
                                      highlightId={highlightId}
                                      highlightRowIndex={highlightRowIndex}
                                      showKebabOnHover={showKebabOnHover}
                                      columnWidthSum={Math.round(
                                        rowColumnWidth
                                      )}
                                    />
                                  );
                                }}
                                columnWidth={rowColumnWidth}
                                columnCount={1}
                                overscanColumnCount={1}
                                __rerender={finalTableProps.rerender}
                                ref={(ref) => {
                                  registerChild(ref);
                                  if (ref !== null) {
                                    gridRef.current = ref;
                                  }
                                }}
                                onSectionRendered={(props) =>
                                  onSectionRendered(props, onRowsRendered)
                                }
                                height={height - heights.headerHeight}
                                style={{
                                  ...(finalTableProps.showFooter &&
                                  !noVerticalScroll
                                    ? { paddingBottom: heights.headerHeight }
                                    : {}),
                                  ...(finalTableProps.isGroupByIndex(0) && {
                                    paddingTop: heights.groupDepth0Spacing,
                                  }),
                                }}
                                width={width}
                                rowCount={totalTableRows}
                                rowHeight={({ index }) => {
                                  const isRowGrouped =
                                    finalTableProps.isGroupByIndex(index);
                                  const isNextRowGrouped =
                                    finalTableProps.isGroupByIndex(index + 1);
                                  const groupSpacing =
                                    isRowGrouped && isNextRowGrouped
                                      ? heights.groupDepth0Spacing
                                      : 0;
                                  const lastOfGroupSpacing =
                                    !isRowGrouped && isNextRowGrouped
                                      ? heights.groupDepth0Spacing
                                      : 0;
                                  return isRowGrouped
                                    ? heights.groupRowHeight + groupSpacing
                                    : heights.rowHeight + lastOfGroupSpacing;
                                }}
                                onScroll={(props) => {
                                  let total =
                                    height -
                                    heights.headerHeight -
                                    scrollbarWidth +
                                    props.scrollTop;
                                  const cilovyRadek = Math.round(
                                    total / heights.rowHeight
                                  );

                                  finalTableProps.setLastVisitedRowIndex?.(
                                    cilovyRadek - 1
                                  );
                                  lastVisitedRowIndexRef.current =
                                    cilovyRadek - 1;
                                  onScroll(props);
                                }}
                                // @ts-ignore
                                scrollToRow={getScrollToHighlight()}
                              />
                            )}
                          </InfiniteLoader>
                          {/*footer*/}
                          {finalTableProps.showFooter && (
                            <Grid
                              className={classNames(Classes.FINAL_TABLE_FOOTER)}
                              cellRenderer={(props) => (
                                <FooterRow
                                  finalTableProps={finalTableProps}
                                  gridCellProps={props}
                                  heights={heights}
                                  formattedColumns={formattedColumns}
                                  columnsWidth={columnsWidth}
                                />
                              )}
                              columnWidth={
                                rowColumnWidth +
                                columnsWidth[formattedColumns.length]
                              }
                              columnCount={1}
                              overscanColumnCount={1}
                              __rerender={finalTableProps.rerender}
                              height={heights.footerHeight}
                              width={width - scrollbarWidth}
                              rowCount={1}
                              rowHeight={heights.footerHeight}
                              scrollLeft={scrollLeft}
                              style={{
                                bottom: noVerticalScroll
                                  ? 0
                                  : heights.footerHeight +
                                    horizontalScrollBarHeight,
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <div
                          //inline styl protože width je z variable
                          style={{ width: noVerticalScroll ? width : '100%' }}
                          className={classNames(
                            Classes.FINAL_TABLE_NO_CONTENT,
                            {
                              [Classes.FINAL_TABLE_POSITION_ABSOLUTE]:
                                !noVerticalScroll,
                            }
                          )}
                        >
                          {!finalTableProps.loading &&
                            finalTableProps.errors.length === 0 && <NoData />}
                          {!finalTableProps.loading &&
                            finalTableProps.errors.length > 0 && (
                              <AppError
                                className={'h-100'}
                                message={
                                  finalTableProps.errors?.reduce(
                                    (acc, x) =>
                                      acc + Number(!!x?.apiError?.messageTitle),
                                    0
                                  ) === 0
                                    ? 'Data se nepodařilo načíst'
                                    : finalTableProps.errors
                                        .map(
                                          (x) =>
                                            x.apiError?.messageTitle ??
                                            x.description
                                        )
                                        .join('\n')
                                }
                              >
                                <div
                                  className={
                                    'd-flex align-items-center flex-column'
                                  }
                                >
                                  {finalTableProps.errors?.reduce(
                                    (acc, x) => acc + Number(!!x?.description),
                                    0
                                  ) > 0 ? (
                                    <a
                                      href={'#'}
                                      onClick={() => {
                                        setShowErrors(!showErrors);
                                      }}
                                    >
                                      {showErrors
                                        ? 'Skrýt podrobnosti'
                                        : 'Zobrazit podrobnosti'}
                                    </a>
                                  ) : null}
                                  {showErrors && (
                                    <div className={'mt-2'}>
                                      {finalTableProps.errors.map(
                                        (error, index) => {
                                          return (
                                            <div style={{ textAlign: 'left' }}>
                                              {index + 1}.{' '}
                                              {error.message ||
                                                'Při načítání dat nastala obecná chyba'}
                                              {error.description && (
                                                <div>
                                                  <small>
                                                    {error.description}
                                                  </small>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  )}
                                </div>
                              </AppError>
                            )}
                        </div>
                      )}
                    </>
                  );
                }}
              </AutoSizer>
            )}
          </ScrollSync>
        </div>
      </div>
    </div>
  );
};

export const FinalTable = forwardRef(FinalTableIn) as <
  DATA extends IData<ID>,
  FILTER extends object = {},
  ID extends unknown = {},
>(
  props: Props<DATA, FILTER, ID> & {
    ref?: React.ForwardedRef<VirtualDataRef<ID>>;
  }
) => ReturnType<typeof FinalTableIn>;
